/**
 * Centralized Search Path Configuration
 *
 * This module provides search paths for all apps across different environments (QA, preprod, production).
 * Search paths are used to filter content in the CMS search API.
 *
 * ## Environment Mapping
 * - ENV = "development" or "staging" → uses QA paths
 * - ENV = "preprod" → uses preprod paths
 * - ENV = "production" → uses production paths
 *
 * ## Usage Example
 * ```typescript
 * import { getSearchPath, getProjectsPath } from 'utils'
 *
 * // Get search path for a specific app based on current environment (process.env.ENV)
 * const searchPath = getSearchPath('ashtrom-corporate')
 *
 * // Get projects path based on current environment
 * const projectsPath = getProjectsPath()
 * ```
 *
 * ## Adding a New App
 * 1. Add the app name to the AppName type
 * 2. Add the app's paths to the SEARCH_PATHS constant
 * 3. Regenerate paths by running: `cd scripts && pnpm tsx fetch-search-paths.ts`
 *
 * ## Regenerating Search Paths
 * To update all paths with the latest data from the CMS:
 * ```bash
 * cd scripts
 * pnpm tsx fetch-search-paths.ts
 * ```
 * This will update search-paths-output.json, which is the source of truth for this configuration.
 */

/**
 * All available app names in the monorepo
 */
export type AppName =
  | "ashtrom-corporate"
  | "ashtrom-construction"
  | "ashtrom-4rent"
  | "ashtrom-ashdar"
  | "ashtrom-industries"
  | "ashtrom-energy"
  | "ashtrom-international"
  | "ashtrom-concessions"
  | "ashtrom-base"
  | "ashtrom-ashcrete"
  | "ashtrom-melet"
  | "ashtrom-engineering"
  | "ashtrom-drachim"
  | "ashtrom-properties"
  | "tierra-bonita"
  | "ashkalit"
  | "ashbond"
  | "israbeton"
  | "itumit"
  | "quarries"
  | "bg-bond"
  | "bg-bond-rebuild"
  | "el-patrimonio"
  | "solars"
  | "risho"
  | "ashtrom-port"
  | "nitzavim"

/**
 * Available environments for search paths
 */
export type Environment = "qa" | "preprod" | "production"

/**
 * Search paths for all apps across all environments.
 * Paths are in the format "-1,pageId1,pageId2,..." where -1 is the root.
 *
 * Source: scripts/search-paths-output.json
 * Last updated: 2025-12-18T10:48:17.237Z
 */
export const SEARCH_PATHS: Record<AppName, Record<Environment, string | null>> = {
  "ashtrom-corporate": {
    qa: "-1,1359",
    preprod: "-1,4803",
    production: "-1,4803",
  },
  "ashtrom-construction": {
    qa: "-1,1837",
    preprod: "-1,4804",
    production: "-1,4804",
  },
  "ashtrom-4rent": {
    qa: "-1,2230",
    preprod: "-1,4806",
    production: "-1,4806",
  },
  "ashtrom-ashdar": {
    qa: "-1,2075",
    preprod: "-1,4805",
    production: "-1,4805",
  },
  "ashtrom-industries": {
    qa: "-1,2548",
    preprod: "-1,4807",
    production: "-1,4807",
  },
  "ashtrom-energy": {
    qa: "-1,2725",
    preprod: "-1,4808",
    production: "-1,4808",
  },
  "ashtrom-international": {
    qa: "-1,2868",
    preprod: "-1,4809",
    production: "-1,4809",
  },
  "ashtrom-concessions": {
    qa: "-1,3068",
    preprod: "-1,4810",
    production: "-1,4810",
  },
  "ashtrom-base": {
    qa: "-1,5504",
    preprod: "-1,4812",
    production: "-1,4812",
  },
  "ashtrom-ashcrete": {
    qa: "-1,5753",
    preprod: "-1,4813",
    production: "-1,4813",
  },
  "ashtrom-melet": {
    qa: "-1,5866",
    preprod: "-1,4814",
    production: "-1,4814",
  },
  "ashtrom-engineering": {
    qa: "-1,5917",
    preprod: "-1,4815",
    production: "-1,4815",
  },
  "ashtrom-drachim": {
    qa: "-1,5991",
    preprod: "-1,4816",
    production: "-1,4816",
  },
  "ashtrom-properties": {
    qa: "-1,3393",
    preprod: "-1,4811",
    production: "-1,4811",
  },
  "tierra-bonita": {
    qa: "-1,9924",
    preprod: "-1,11593",
    production: "-1,11593",
  },
  ashkalit: {
    qa: "-1,10124",
    preprod: "-1,13925",
    production: "-1,13925",
  },
  ashbond: {
    qa: "-1,10181",
    preprod: "-1,13971",
    production: "-1,13971",
  },
  israbeton: {
    qa: "-1,10270",
    preprod: "-1,14081",
    production: "-1,14081",
  },
  itumit: {
    qa: "-1,10359",
    preprod: "-1,14184",
    production: "-1,14184",
  },
  quarries: {
    qa: "-1,10411",
    preprod: "-1,14236",
    production: "-1,14236",
  },
  "bg-bond": {
    qa: "-1,10486",
    preprod: "-1,14377",
    production: "-1,14377",
  },
  "bg-bond-rebuild": {
    qa: null,
    preprod: null,
    production: null,
  },
  "el-patrimonio": {
    qa: "-1,10734",
    preprod: "-1,14335",
    production: "-1,14335",
  },
  solars: {
    qa: "-1,10823",
    preprod: "-1,15372",
    production: "-1,15372",
  },
  risho: {
    qa: null,
    preprod: null,
    production: null,
  },
  "ashtrom-port": {
    qa: "-1,11389",
    preprod: null,
    production: null,
  },
  nitzavim: {
    qa: null,
    preprod: null,
    production: null,
  },
}

/**
 * Projects search paths across all environments.
 * Used for the projects lobby and project pages.
 *
 * Source: scripts/search-paths-output.json
 */
export const PROJECTS_PATHS: Record<Environment, string> = {
  qa: "-1,1540,1543,1556",
  preprod: "-1,4802,4823,4861",
  production: "-1,4802,4823,4861",
}

/**
 * Determine environment from ENV variable.
 *
 * @param env - The ENV variable value (e.g., "development", "staging", "preprod", "production")
 * @returns The mapped environment (qa, preprod, or production)
 *
 * @internal
 */
function getEnvironmentFromEnv(env?: string): Environment {
  if (!env) return "qa"

  if (env === "production") return "production"
  if (env === "preprod") return "preprod"

  // "development", "staging", or anything else → qa
  return "qa"
}

/**
 * Get search path for a specific app based on the current environment.
 *
 * The environment is determined from process.env.ENV:
 * - "production" → production paths
 * - "preprod" → preprod paths
 * - "development", "staging", or anything else → qa paths
 *
 * @param appName - The app name (e.g., "ashtrom-corporate")
 * @returns Search path string (e.g., "-1,4803,4819") or "-1" as fallback
 *
 * @example
 * ```typescript
 * // Get path for current environment (from process.env.ENV)
 * const path = getSearchPath('ashtrom-corporate')
 * ```
 */
export function getSearchPath(appName: AppName): string {
  const environment = getEnvironmentFromEnv(process.env.ENV)
  const path = SEARCH_PATHS[appName]?.[environment]

  if (!path) {
    console.warn(
      `Search path not found for app: ${appName}, environment: ${environment}. Using fallback: "-1"`,
    )
    return "-1" // Fallback to root only
  }

  return path
}

/**
 * Get projects search path based on the current environment.
 *
 * The environment is determined from process.env.ENV:
 * - "production" → production paths
 * - "preprod" → preprod paths
 * - "development", "staging", or anything else → qa paths
 *
 * @returns Projects path string (e.g., "-1,4802,4823,4861")
 *
 * @example
 * ```typescript
 * // Get projects path for current environment
 * const projectsPath = getProjectsPath()
 * ```
 */
export function getProjectsPath(): string {
  const environment = getEnvironmentFromEnv(process.env.ENV)
  return PROJECTS_PATHS[environment]
}
